import React from 'react';
import { Link, useParams, Outlet } from 'react-router-dom';
import {
  ArrowLeftRight,
  ArrowUpDown,
  Menu,
  X,
  Mail,
  Heart
} from 'lucide-react';

export function Layout() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { category = 'length' } = useParams<{ category: string }>();

  function ToolsNavigation() {
    return (
      <nav className="flex space-x-4">
        <Link 
          to={`/${category}/calculator`}
          className="text-gray-600 hover:text-blue-600"
        >
          Calculator
        </Link>
        <Link 
          to={`/${category}/compare`}
          className="text-gray-600 hover:text-blue-600"
        >
          Compare
        </Link>
        <Link 
          to={`/${category}/advanced`}
          className="text-gray-600 hover:text-blue-600"
        >
          Advanced
        </Link>
      </nav>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <header className="bg-white/70 backdrop-blur-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white shadow-lg shadow-blue-500/25 group-hover:shadow-xl group-hover:shadow-blue-500/30 transition-all">
                <div className="relative w-6 h-6">
                  <ArrowLeftRight className="w-6 h-6 absolute inset-0 transform rotate-0 transition-transform group-hover:rotate-90 duration-300" />
                  <ArrowUpDown className="w-6 h-6 absolute inset-0 transform rotate-90 opacity-50 group-hover:rotate-180 duration-300" />
                </div>
              </div>
              <span className="text-lg sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:from-blue-500 group-hover:to-indigo-500 transition-all">
                Easy Unit Converter
              </span>
            </Link>
            <nav className="hidden md:flex items-center gap-2">
              <Link
                to={`/tables/${category}`}
                className="px-4 py-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Tables
              </Link>
              <Link
                to="/faq"
                className="px-4 py-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                FAQ
              </Link>
              <Link
                to="/blog"
                className="px-4 py-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Blog
              </Link>
            </nav>
          </div>
          
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-gray-600" />
            ) : (
              <Menu className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-100">
            <nav className="flex flex-col p-4 bg-white/95 backdrop-blur-sm">
              <Link
                to={`/tables/${category}`}
                className="px-4 py-3 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Tables
              </Link>
              <Link
                to="/faq"
                className="px-4 py-3 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                FAQ
              </Link>
              <Link
                to="/blog"
                className="px-4 py-3 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Blog
              </Link>
            </nav>
          </div>
        )}
      </header>

      <Outlet />

      <footer className="mt-auto bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white">
                  <div className="relative w-5 h-5">
                    <ArrowLeftRight className="w-5 h-5 absolute inset-0" />
                    <ArrowUpDown className="w-5 h-5 absolute inset-0 opacity-50" />
                  </div>
                </div>
                <span className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                  Easy Unit Converter
                </span>
              </Link>
              <p className="text-sm text-gray-600">
                Free online unit converter with over 50 categories and thousands of conversion options.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/length" className="text-gray-600 hover:text-blue-600 text-sm">Length Converter</Link>
                </li>
                <li>
                  <Link to="/weight" className="text-gray-600 hover:text-blue-600 text-sm">Weight Converter</Link>
                </li>
                <li>
                  <Link to="/temperature" className="text-gray-600 hover:text-blue-600 text-sm">Temperature Converter</Link>
                </li>
                <li>
                  <Link to="/cooking" className="text-gray-600 hover:text-blue-600 text-sm">Cooking Converter</Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Resources</h3>
              <ul className="space-y-2">
                <li><Link to="/faq" className="text-gray-600 hover:text-blue-600 text-sm">FAQ</Link></li>
                <li><Link to="/tables/length" className="text-gray-600 hover:text-blue-600 text-sm">Conversion Tables</Link></li>
                <li>
                  <Link to="/blog" className="text-gray-600 hover:text-blue-600 text-sm">
                    Blog
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Contact</h3>
              <ul className="space-y-2">
                <li>
                  <a href="mailto:aristomenis.georgiopoulos@gmail.com" className="text-gray-600 hover:text-blue-600 text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </a>
                </li>
                <li><Link to="/privacy" className="text-gray-600 hover:text-blue-600 text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-600 hover:text-blue-600 text-sm">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600">
                © {new Date().getFullYear()} Easy Unit Converter. Made with <Heart className="w-4 h-4 inline text-red-500" /> by developers for developers.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}